"use client";

import {useSelectedLayoutSegments} from "next/navigation";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {GailProductRoles, Permissions, RoleData} from "@/enums/user";
import {useBrandConfig} from "@/hooks/useBrandConfig";
import {useEodReportAlert} from "@/hooks/useEodReportAlert";
import {useOrg} from "@/hooks/useOrg";
import {usePermissions} from "@/hooks/usePermissions";
import type {GlobalNavItemType, GlobalNavSectionType} from "@/types/common/library";
import {canAccessNavItem} from "@/utils/authz/navAccess";
import {CREATE_OR_UPDATE_LEVELS} from "@/utils/authz/routeGates";
import {GetGailGPTBrandName} from "@/utils/chat";
import {BASE_SUPPORT_URL} from "@/utils/common/baseUrls";
import {useAssistantShortcutHint} from "@/hooks/useAssistantShortcutHint";
import {
   canSeeReportingNav,
   getReportingNavHref,
} from "@/utils/reporting/access";
import BaseNavContainer from "./BaseNavContainer";

const isActive = (segments: string[], target: string, position: number = 0) =>
   segments.length > position && segments[position] === target;

export default function GailGptNavContainer({
   children,
   onUserManagementClick,
}: {
   children: React.ReactNode;
   onUserManagementClick?: () => void;
}) {
   const segments = useSelectedLayoutSegments();
   const {productRolesState} = useAuthContext();
   const {ui, productAccess} = useOrg();
   const {config: brandConfig} = useBrandConfig(ui.brand);
   const {canAccess, canAccessAny, permissions} = usePermissions();
   const {hasAlert: hasEodReportAlert} = useEodReportAlert();
   const {isGailGptDockEnabled, isDockAvailable, isOpen: isDockOpen, toggleDock} = useGailDock();

   const {t} = useTranslation("routes");
   const {t: ct} = useTranslation("common");
   const assistantShortcutHint = useAssistantShortcutHint();
   const isReportUser =
      productRolesState?.roles?.includes(GailProductRoles.ReportUser) ?? false;
   const gailAgentEnabled = productAccess.gailAgentEnabled ?? false;
   const showReporting = canSeeReportingNav(
      gailAgentEnabled,
      isReportUser,
      canAccess(RoleData.Reporting, Permissions.Read)
   );

   const buildMainSections = () => {
      const reportingSection: GlobalNavSectionType | null = showReporting
         ? {
              name: t("dataAndAnalytics"),
              position: "top",
              items: [
                 {
                    icon: "chart-pie-simple",
                    label: ct("reporting"),
                    href: getReportingNavHref(gailAgentEnabled, isReportUser),
                    dataType: RoleData.Reporting,
                    isActive: isActive(segments, "reporting"),
                    hasAlert: hasEodReportAlert,
                 },
              ],
           }
         : null;

      const brandName = GetGailGPTBrandName(ui.brand, t, brandConfig);

      // Away from the full-page chat, the assistant is a single distinctive
      // entry that summons the dock beside the current page (New Chat and
      // History live in the dock header). On full-page GailGPT routes the
      // dock can't be summoned, so the direct links stay.
      const gptItems: GlobalNavItemType[] = isDockAvailable
         ? [
              {
                 icon: "wand-magic-sparkles",
                 label: brandName,
                 onClick: toggleDock,
                 isActive: isDockOpen,
                 dataType: RoleData.GPT,
                 variant: "assistant",
                 shortcutHint: assistantShortcutHint,
              } satisfies GlobalNavItemType,
           ]
         : [
              {
                 icon: "comment",
                 label: t("newChat"),
                 href: "/gailgpt",
                 isActive: isActive(segments, "gailgpt"),
                 dataType: RoleData.GPT,
              },
              {
                 icon: "clock",
                 label: t("history"),
                 href: "/history",
                 isActive: isActive(segments, "history"),
                 dataAttributes: {
                    "data-tour": "history-nav",
                 },
                 dataType: RoleData.GPT,
              },
           ];

      const connectionItems: GlobalNavItemType[] = [
         {
            icon: "user-headset",
            label: t("gails"),
            href: "/assistants",
            isActive: isActive(segments, "assistants"),
            dataAttributes: {
               "data-tour": "gails-nav",
            },
            dataType: RoleData.Assistants,
            levels: CREATE_OR_UPDATE_LEVELS,
         },
         {
            icon: "puzzle-piece",
            label: t("skills"),
            href: "/skills",
            isActive: isActive(segments, "skills"),
            dataType: RoleData.Assistants,
            levels: CREATE_OR_UPDATE_LEVELS,
         },
         {
            icon: "book",
            label: t("knowledge"),
            href: "/knowledge/files",
            isActive: isActive(segments, "knowledge"),
            dataType: RoleData.Knowledge,
            additionalDataTypes: [RoleData.Agent, RoleData.Settings],
            levels: CREATE_OR_UPDATE_LEVELS,
         },
         {
            icon: "wrench",
            label: t("integrations"),
            href: "/integrations/tools",
            isActive: isActive(segments, "integrations"),
            dataType: RoleData.Integrations,
         },
         {
            icon: "circle-nodes",
            label: t("workflows"),
            href: "/workflows",
            isActive: isActive(segments, "workflows"),
            dataType: RoleData.Workflows,
         },
      ];

      const gptSection: GlobalNavSectionType = {
         // No header when consolidated to the single assistant entry — the
         // entry itself carries the brand name.
         name: isDockAvailable ? undefined : GetGailGPTBrandName(ui.brand, t, brandConfig),
         position: "top",
         items: gptItems,
      };

      const connectionSection: GlobalNavSectionType = {
         name: t("general"),
         position: "top",
         items: connectionItems,
      };

      // With GailGPT dock on, the assistant entry is pinned first;
      // otherwise the GailGPT section sits after reporting.
      const withReporting = (sections: GlobalNavSectionType[]) =>
         reportingSection ? [reportingSection, ...sections] : sections;
      const withReportingAfterGpt = (sections: GlobalNavSectionType[]) => {
         if (!reportingSection) return sections;
         const [gpt, ...rest] = sections;
         return gpt ? [gpt, reportingSection, ...rest] : [reportingSection, ...rest];
      };

      return isGailGptDockEnabled
         ? withReportingAfterGpt([gptSection, connectionSection])
         : withReporting([gptSection, connectionSection]);
   };

   const buildResourcesSection = () => {
      const resources: GlobalNavSectionType = {
         position: "bottom",
         items: [
            {
               icon: "gear" as const,
               label: t("settings"),
               href: "/settings/general",
               isActive: isActive(segments, "settings"),
               dataType: RoleData.Settings,
            },
            {
               icon: "headset",
               label: t("support"),
               href: BASE_SUPPORT_URL,
               target: "_blank",
               dataType: RoleData.Support,
            },
         ],
      };

      return resources;
   };

   const sections = useMemo<GlobalNavSectionType[]>(() => {
      let navSections: GlobalNavSectionType[] = [];

      navSections.push(...buildMainSections());
      navSections.push(buildResourcesSection());

      //Go through sections and remove any sections
      // that the user does not have access to based on the hook
      navSections.forEach((section) => {
         section.items = section.items.filter((item) => {
            if (item.dataType === RoleData.Reporting) {
               return showReporting;
            }
            return canAccessNavItem(item, canAccessAny);
         });
      });

      //Remove any sections that have no items
      navSections = navSections.filter((section) => section.items.length > 0);

      return navSections;
   }, [
      segments,
      t,
      ct,
      ui.brand,
      brandConfig,
      permissions,
      hasEodReportAlert,
      isGailGptDockEnabled,
      isDockAvailable,
      isDockOpen,
      toggleDock,
      assistantShortcutHint,
      gailAgentEnabled,
      isReportUser,
      showReporting,
   ]);

   return (
      <BaseNavContainer menuSections={sections} onUserManagementClick={onUserManagementClick}>
         {children}
      </BaseNavContainer>
   );
}
